import React from 'react';
import { Alert, Card, Col, Descriptions, Divider, Progress, Row, Space, Statistic, Table, Tag, Typography, theme } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, DashboardOutlined, GlobalOutlined, WarningOutlined } from '@ant-design/icons';
import { CheckedBy, DiagResponse, PingResult, checkedFromLabel } from './model';

const { Text } = Typography;

const statLabel = (s: string) => <Text type="secondary" style={{ fontSize: 12 }}>{s}</Text>;
const STAT_VALUE: React.CSSProperties = { fontSize: 18, lineHeight: 1.2 };
// Typography renders `code` at 85% of its own size: 14px lands on ~12px.
const CODE: React.CSSProperties = { fontSize: 14 };

/** PingResultPanel renders the outcome of an ICMP diagnostic: one stats strip,
 *  the run's system context, the per-target table and any troubleshooting hints.
 *  Purely presentational — it derives its numbers from the response it is given. */
const PingResultPanel: React.FC<{ result: DiagResponse; checkedBy: CheckedBy | null }> = ({ result, checkedBy }) => {
  const { token } = theme.useToken();

  const successRate = result.totalTargets > 0 ? result.succeeded / result.totalTargets : 0;
  const successPct = Math.round(successRate * 100);
  const statusColor = successPct > 80 ? token.colorSuccess : successPct > 50 ? token.colorWarning : token.colorError;
  const statusText = successPct > 80 ? 'OK' : successPct > 50 ? 'Degraded' : 'Failed';

  const ok = result.results?.filter((r) => r.success && r.latencyMs > 0) || [];
  const avgLatency = ok.length > 0 ? ok.reduce((sum, r) => sum + r.latencyMs, 0) / ok.length : 0;
  const minLatency = ok.length > 0 ? Math.min(...ok.map((r) => r.latencyMs)) : 0;
  const maxLatency = ok.length > 0 ? Math.max(...ok.map((r) => r.latencyMs)) : 0;
  const maxDuration = result.results ? Math.max(...result.results.map((r) => r.durationMs)) : 0;

  // Detect specific error patterns for troubleshooting
  const failures = result.results?.filter((r) => !r.success) || [];
  const hasPermissionError = failures.some((r) => r.error?.includes('operation not permitted') || r.error?.includes('permission denied'));
  const hasSocketError = failures.some((r) => r.error?.includes('socket') || r.error?.includes('too many open files'));
  const hasTimeoutError = failures.some((r) => r.error?.includes('timeout'));
  const hasCreateError = failures.some((r) => r.error?.includes('create:'));

  return (
    <>
      {/* Stats strip and run context share one card: six bordered cards plus a
          second card cost ~200px of height for two lines of numbers. */}
      <Card size="small" style={{ marginBottom: 12 }} styles={{ body: { padding: 12 } }}>
        <Row gutter={12}>
          <Col span={4}>
            <Statistic
              title={statLabel('Result')}
              value={statusText}
              valueStyle={{ ...STAT_VALUE, color: statusColor }}
              prefix={successPct > 80 ? <CheckCircleOutlined /> : successPct > 50 ? <WarningOutlined /> : <CloseCircleOutlined />}
            />
            <Progress percent={successPct} strokeColor={statusColor} showInfo={false} size="small" style={{ marginTop: 4 }} />
          </Col>
          <Col span={4}>
            <Statistic
              title={statLabel('Succeeded')}
              value={result.succeeded}
              suffix={`/ ${result.totalTargets}`}
              valueStyle={{ ...STAT_VALUE, color: result.failed > 0 ? token.colorWarning : token.colorSuccess }}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title={statLabel('Failed')}
              value={result.failed}
              valueStyle={{ ...STAT_VALUE, color: result.failed > 0 ? token.colorError : token.colorSuccess }}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title={statLabel('Avg latency')}
              value={avgLatency > 0 ? avgLatency.toFixed(1) : '—'}
              suffix={avgLatency > 0 ? 'ms' : ''}
              valueStyle={STAT_VALUE}
              prefix={<DashboardOutlined />}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title={statLabel('Min / max latency')}
              value={minLatency > 0 ? `${minLatency.toFixed(1)} / ${maxLatency.toFixed(1)}` : '—'}
              suffix={minLatency > 0 ? 'ms' : ''}
              valueStyle={STAT_VALUE}
            />
          </Col>
          <Col span={4}>
            <Statistic title={statLabel('Total duration')} value={result.totalDurationMs} suffix="ms" valueStyle={STAT_VALUE} />
          </Col>
        </Row>

        <Divider style={{ margin: '10px 0' }} />

        <Descriptions column={4} size="small" styles={{ label: { fontSize: 12 }, content: { fontSize: 12 } }}>
          <Descriptions.Item label="Checked from">
            <Space size={4}>
              <GlobalOutlined />
              <Text strong style={{ fontSize: 12 }}>{checkedFromLabel(checkedBy) || 'unknown worker'}</Text>
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Concurrency">{result.concurrency}</Descriptions.Item>
          <Descriptions.Item label="Ping limiter cap">{result.pingLimiterCap} slots</Descriptions.Item>
          <Descriptions.Item label="Workers busy">{result.workersBusy} / {result.workersTotal}</Descriptions.Item>
          <Descriptions.Item label="Slowest ping">{maxDuration} ms</Descriptions.Item>
        </Descriptions>

        {result.concurrency > result.pingLimiterCap && (
          <Alert
            message={`Test concurrency (${result.concurrency}) exceeds ping limiter cap (${result.pingLimiterCap}). If the test succeeds but workers are idle, the ping limiter is the bottleneck — increase worker count or adjust the 40% ratio.`}
            type="info"
            showIcon
            style={{ marginTop: 8, fontSize: 12 }}
          />
        )}
      </Card>

      <Card title={<Text style={{ fontSize: 13 }}>Per-target results</Text>} size="small" style={{ marginBottom: 12 }} styles={{ body: { padding: 0 } }}>
        <Table
          dataSource={result.results.map((r, i) => ({ ...r, key: i }))}
          size="small"
          // defaultPageSize, not pageSize: above 50 rows antd shows its own
          // size changer, and a fixed pageSize prop overrides whatever the
          // operator picks in it.
          pagination={result.results.length > 50 ? { defaultPageSize: 50, size: 'small' } : false}
          scroll={{ y: 300 }}
          columns={[
            {
              title: 'Target', dataIndex: 'target', width: 150, ellipsis: true,
              render: (v: string) => <Text code style={CODE}>{v}</Text>,
            },
            {
              title: 'Status', dataIndex: 'success', width: 84,
              render: (v: boolean) => v
                ? <Tag icon={<CheckCircleOutlined />} color="success" style={{ margin: 0, fontSize: 12 }}>OK</Tag>
                : <Tag icon={<CloseCircleOutlined />} color="error" style={{ margin: 0, fontSize: 12 }}>FAIL</Tag>,
              filters: [
                { text: 'OK', value: true },
                { text: 'FAIL', value: false },
              ],
              onFilter: (value: any, record: any) => record.success === value,
            },
            {
              title: 'Latency', dataIndex: 'latencyMs', width: 96, align: 'right' as const,
              sorter: (a: PingResult, b: PingResult) => a.latencyMs - b.latencyMs,
              render: (v: number) => v > 0 ? (
                <Text style={{ fontSize: 12, color: v > 500 ? token.colorError : v > 200 ? token.colorWarning : token.colorSuccess }}>
                  {v.toFixed(1)} ms
                </Text>
              ) : '—',
            },
            {
              title: 'Loss', dataIndex: 'packetLoss', width: 82, align: 'right' as const,
              sorter: (a: PingResult, b: PingResult) => a.packetLoss - b.packetLoss,
              render: (v: number) => (
                <Text style={{ fontSize: 12, color: v > 0 ? (v >= 100 ? token.colorError : token.colorWarning) : undefined }}>
                  {v.toFixed(0)}%
                </Text>
              ),
            },
            {
              title: 'Duration', dataIndex: 'durationMs', width: 96, align: 'right' as const,
              sorter: (a: PingResult, b: PingResult) => a.durationMs - b.durationMs,
              render: (v: number) => (
                <Text style={{ fontSize: 12, color: v > 10000 ? token.colorError : v > 5000 ? token.colorWarning : undefined }}>
                  {v} ms
                </Text>
              ),
            },
            {
              title: 'Error', dataIndex: 'error', ellipsis: true,
              render: (v: string) => v
                ? <Text type="danger" style={{ fontSize: 12 }} ellipsis={{ tooltip: v }}>{v}</Text>
                : null,
            },
          ]}
        />
      </Card>

      {result.failed > 0 && (
        <Card
          title={<Space size={6}><WarningOutlined style={{ color: token.colorWarning }} /><Text style={{ fontSize: 13 }}>Troubleshooting</Text></Space>}
          size="small"
        >
          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            {hasPermissionError && (
              <Alert
                message="Permission denied"
                description={(
                  <>
                    ICMP raw sockets require elevated privileges. Add <Text code style={CODE}>cap_add: [NET_RAW]</Text> to
                    the checker service in <Text code style={CODE}>docker-compose.yml</Text>, or run with <Text code style={CODE}>--privileged</Text>.
                  </>
                )}
                type="error"
                showIcon
              />
            )}
            {hasSocketError && (
              <Alert
                message="Socket limit reached"
                description={(
                  <>
                    The OS is limiting concurrent ICMP sockets. Try reducing test concurrency,
                    or increase the system limit: <Text code style={CODE}>sysctl -w net.ipv4.ping_group_range=&quot;0 2147483647&quot;</Text>
                    and increase <Text code style={CODE}>ulimit -n</Text> (open files).
                  </>
                )}
                type="warning"
                showIcon
              />
            )}
            {hasTimeoutError && (
              <Alert
                message="Timeout"
                description="Some pings timed out. The network may be congested, or targets may be unreachable from the checker host. Check firewall rules and routing."
                type="warning"
                showIcon
              />
            )}
            {hasCreateError && !hasPermissionError && (
              <Alert
                message="Pinger creation failed"
                description="Could not create ICMP pinger. Check that the target IPs are valid and that DNS resolution works from the checker container."
                type="warning"
                showIcon
              />
            )}
            {!hasPermissionError && !hasSocketError && !hasTimeoutError && !hasCreateError && (
              <Alert
                message="Some pings failed"
                description="Check the Error column in the results table for specific failure details."
                type="info"
                showIcon
              />
            )}
          </Space>
        </Card>
      )}

      {result.failed === 0 && result.succeeded > 0 && (
        <Alert
          message="All pings succeeded"
          description={
            result.workersBusy < result.workersTotal * 0.5
              ? `All ${result.succeeded} raw pings passed, but only ${result.workersBusy}/${result.workersTotal} workers are busy. The bottleneck is likely the application's internal ping limiter (${result.pingLimiterCap} slots) or external API rate limiters (Globalping: 10, GeoIP: 15).`
              : `All ${result.succeeded} raw pings passed with ${result.concurrency} concurrency. The checker server's ICMP capability is healthy.`
          }
          type="success"
          showIcon
          style={{ marginTop: 12 }}
        />
      )}
    </>
  );
};

export default PingResultPanel;
